// Pre-snapshot screens: shown while the initial `GET /state` is in
// flight (LoadingScreen) or when it fails (ErrorScreen). Mounted by
// `BattlePage` instead of the main board grid.

import type { pixelTheme } from "../../theme/pixel";
import { primaryBtn, withBtnStyle } from "../../theme/styles";

export function LoadingScreen({ theme }: { theme: typeof pixelTheme }) {
  return (
    <div
      style={{
        height: "100%",
        background: theme.pageBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontDisplay,
        color: theme.inkDim,
        fontSize: 14,
        letterSpacing: theme.letterSpacing,
      }}
    >
      {theme.ornament} Loading…
    </div>
  );
}

// Turn a raw error reason (an engine/channel code or a thrown message)
// into a sentence a player can act on. Unknown strings pass through so
// we never hide a genuine stack message during development.
function friendlyError(error: string): { heading: string; body: string } {
  switch (error) {
    case "engine_error":
      return {
        heading: "Battle unavailable",
        body: "This battle is no longer available — the game server may have restarted. Head back to the lobby to start a new one.",
      };
    case "timeout":
      return {
        heading: "Battle unavailable",
        body: "Couldn't reach the battle — the connection timed out. Head back to the lobby and try again.",
      };
    case "idle_evicted":
      return {
        heading: "Battle ended",
        body: "This battle was left idle for too long and has been closed. Head back to the lobby to start a new one.",
      };
    case "superseded":
      return {
        heading: "Battle ended",
        body: "This battle ended because a new one was started. Head back to the lobby to continue.",
      };
    case "unrecoverable":
      return {
        heading: "Battle unavailable",
        body: "This battle could no longer be restored — the game server may have restarted. Head back to the lobby to start a new one.",
      };
    default:
      return { heading: "Something broke", body: error };
  }
}

export function ErrorScreen({
  error,
  onBack,
  theme,
}: {
  error: string;
  onBack: () => void;
  theme: typeof pixelTheme;
}) {
  const { heading, body } = friendlyError(error);
  return (
    <div
      style={{
        height: "100%",
        background: theme.pageBg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        fontFamily: theme.fontDisplay,
        color: theme.ink,
        padding: 20,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 18, color: theme.accent }}>
        {theme.ornament} {heading}
      </div>
      <pre
        style={{
          fontFamily: theme.fontBody,
          fontSize: 14,
          background: theme.bgDeep,
          padding: 16,
          border: `2px solid ${theme.panelEdgeStrong}`,
          maxWidth: 600,
          whiteSpace: "pre-wrap",
        }}
      >
        {body}
      </pre>
      <button
        onClick={onBack}
        {...withBtnStyle(primaryBtn(theme), { padding: "10px 22px", fontSize: 14 })}
      >
        BACK TO LOBBY
      </button>
    </div>
  );
}
